/**
 * Servicio de un gestor de la sesión (`user.gestores[].service`). TypeScript puro.
 *
 * Es la ÚNICA lista de servicios que el front reconoce para dar acceso. Antes cada
 * predicado tenía su "si no es TPV/PAYMENTS/CARDS → es SPEI", así que un servicio que el
 * back agregara mañana (o un valor mal escrito) daba la línea SPEI, su dashboard y su
 * menú sin que nadie lo decidiera. Ahora un servicio desconocido no da NADA: en acceso,
 * "no lo reconozco" es el lado seguro.
 *
 * `''` es el gestor legacy (payload viejo sin `service`), que históricamente es SPEI.
 */
export const SERVICIOS_DE_GESTOR = ['', 'SPEI', 'CARDS', 'TPV', 'PAYMENTS'] as const;
export type ServicioDeGestor = (typeof SERVICIOS_DE_GESTOR)[number];

const CONOCIDOS: ReadonlySet<string> = new Set(SERVICIOS_DE_GESTOR);

/**
 * Servicios que cuentan como la línea SPEI ("Envíos › SPEI"): legacy, SPEI y CARDS
 * (CARDS se sigue agrupando con SPEI hasta que Tarjetas se separe — fuera de esta feature).
 */
export const SERVICIOS_DE_LINEA_SPEI: ReadonlySet<ServicioDeGestor> = new Set<ServicioDeGestor>(['', 'SPEI', 'CARDS']);

/** Servicios SPEI en sentido ESTRICTO (producto SPEI y sus proveedores): legacy y SPEI. */
export const SERVICIOS_SPEI_ESTRICTO: ReadonlySet<ServicioDeGestor> = new Set<ServicioDeGestor>(['', 'SPEI']);

function esConocido(valor: string): valor is ServicioDeGestor {
  return CONOCIDOS.has(valor);
}

/**
 * Servicio normalizado (`trim().toUpperCase()`, ausente → `''`) o `null` si no es uno de
 * los conocidos. Un `' tpv '` es `'TPV'` en TODOS los predicados.
 */
export function servicioDeGestor(valor: unknown): ServicioDeGestor | null {
  const normalizado = String(valor ?? '').trim().toUpperCase();
  return esConocido(normalizado) ? normalizado : null;
}
